import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  ImageList,
  ImageListItem,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  Inventory as StockIcon,
} from '@mui/icons-material';
import ProductService from '../../services/product.service';

const ProductView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ProductService.getOne(Number(id));
      setProduct(response.data?.data);
    } catch (error: any) {
      console.error('Error fetching product:', error);
      setError(error.response?.data?.error?.message || 'Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await ProductService.delete(Number(id));
        navigate('/products');
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Failed to delete product');
      }
    }
  };

  const formatCurrency = (amount: number, currency: string = 'OMR') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'OMR',
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/products')}
          sx={{ mb: 2 }}
        >
          Back to Products
        </Button>
        <Alert severity="error">{error || 'Product not found'}</Alert>
      </Box>
    );
  }

  const data = product.attributes;
  const images = data.images?.data || [];
  const mainImage = images[selectedImage]?.attributes?.url
    ? `${(process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '')}${images[selectedImage].attributes.url}`
    : '/placeholder.png';

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/products')}>
            Back
          </Button>
          <Typography variant="h4">{data.name}</Typography>
          <Chip
            label={data.isActive ? 'Active' : 'Inactive'}
            color={data.isActive ? 'success' : 'default'}
            size="medium"
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/products/${id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Stack>
      </Stack>

      {/* Main Content */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Images Section */}
        <Box flex={1}>
          <Paper sx={{ p: 2 }}>
            {/* Main Image */}
            <Box
              component="img"
              src={mainImage}
              alt={data.name}
              sx={{
                width: '100%',
                height: 400,
                objectFit: 'contain',
                backgroundColor: 'grey.100',
                borderRadius: 1,
                mb: 2,
              }}
            />

            {/* Thumbnail Images */}
            {images.length > 1 && (
              <ImageList cols={4} gap={8}>
                {images.map((image: any, index: number) => (
                  <ImageListItem
                    key={image.id}
                    onClick={() => setSelectedImage(index)}
                    sx={{
                      cursor: 'pointer',
                      border: selectedImage === index ? 3 : 1,
                      borderColor: selectedImage === index ? 'primary.main' : 'grey.300',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={`${(process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '')}${image.attributes?.url}`}
                      alt={`Thumbnail ${index + 1}`}
                      style={{ height: 80, objectFit: 'cover' }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Paper>
        </Box>

        {/* Details Section */}
        <Box flex={1}>
          <Stack spacing={3}>
            {/* Pricing Card */}
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Selling Price
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {formatCurrency(data.price, data.currency)}
                    </Typography>
                  </Box>
                  {data.cost && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Cost Price
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(data.cost, data.currency)}
                      </Typography>
                    </Box>
                  )}
                  {data.cost && data.price && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Profit Margin
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatCurrency(data.price - data.cost, data.currency)} (
                        {(((data.price - data.cost) / data.price) * 100).toFixed(1)}%)
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Product Information */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <CartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Product Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    SKU
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {data.sku || '-'}
                  </Typography>
                </Box>

                {data.barcode && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Barcode
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.barcode}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Unit
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {data.unit || 'piece'}
                  </Typography>
                </Box>

                {data.weight && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Weight
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.weight} kg
                    </Typography>
                  </Box>
                )}

                {data.dimensions && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Dimensions
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.dimensions}
                    </Typography>
                  </Box>
                )}

                {data.category?.data && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Category
                    </Typography>
                    <Chip 
                      label={data.category.data.attributes?.name} 
                      size="small"
                      clickable
                      onClick={() => navigate(`/categories/${data.category.data.id}`)}
                      color="primary"
                    />
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Stock Information */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <StockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Stock Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Minimum Stock Level
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {data.minStockLevel || 0} {data.unit || 'pieces'}
                  </Typography>
                </Box>

                {data.supplier?.data && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Supplier
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.supplier.data.attributes?.name}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Description */}
            {data.description && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Description
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  {data.description}
                </Typography>
              </Paper>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default ProductView;

